import store from '@/store';
import { isEmpty } from 'lodash';
import { AnyProduct, MainProductPax } from '@shared/interfaces/product';
import { getCurrentMarketSpecs } from '@shared/config/market/market.helpers';
import { getShoppingCartUrlParam } from '@/helpers/queryStrings';
import {
    ActivityProduct,
    Cart,
    FerryPax,
    FerryProduct,
    FlightLeg,
    FlightPax,
    FlightProduct,
    HotelProduct,
    ProductType,
} from './cart.interfaces';
import {
    convertToDate,
    getFormattedDate,
    getShortDate,
} from '@shared/helpers/datetime/datetime';
import { getAgeConfigByProduct } from '@/helpers/dobValidation';
import { AgeRange, AgeRangeDefinitions } from '@/api/Checkout/checkout.interfaces';
import { Markets } from '@shared/config/market/market.models';
import { getCartProductCombination } from '@shared/helpers/productCombination';
import {
    CartProductCombination,
    ProductCombinationType,
} from '@shared/interfaces/productCombination';
import {
    canSetInitialValues,
    removeSessionCart,
    saveCartToSession,
    updatedValuesFromPreviousCart,
} from '@/helpers/cartSession';
import { logStatsigCheckoutCartSessionUpdate } from '@/tracking/statsig/checkout.functions';
import { getObjectDiff } from '@/helpers/object';
import { consoleLog, LogColorType } from '@shared/helpers/log';
import { DateFormat } from '@shared/interfaces/dates';
import { configBase } from '@/config/base';
import { isCheckoutPage, isWhiteLabel } from '@/helpers/url';
import { isChannelMetaHotel } from '@/helpers/affiliate';
import { LogError } from '@/config/errorHandling';
import { StatsigEvent } from '@/tracking/statsig/types';
import { JavascriptError } from '@/api/Errors/errors.interfaces';
import { ItineraryBookData } from '@/api/Itinerary/itinerary.interfaces';

const getInitialNewsLetterState = (cart: Cart) => {
    const isDanishMarket = [
        Markets.DENMARK,
        Markets.STENADENMARK,
        Markets.DTFDENMARK,
    ].includes(getCurrentMarketSpecs().number);
    const { Email, PhoneNumber, ReceiveNewsletter } = cart.Customer;
    if (!!Email && !!PhoneNumber) {
        return ReceiveNewsletter;
    }

    return !isDanishMarket && !cart.Agency;
};

const getShoppingCartKeyFromUrl = (cart: Cart) => {
    return cart?.ShoppingCartKey ?? getShoppingCartUrlParam();
};

const getInitialProductCombination = (cart: Cart): CartProductCombination | null => {
    if (cart.Products && !isEmpty(cart.Products)) {
        const types = cart.Products.map((x) => x.Type);
        return getCartProductCombination(types);
    }

    return null;
};

export function updateCurrentSessionCart() {
    const clearSessionCart: boolean = store.getters['checkout/getClearSessionCart'];

    if (clearSessionCart) {
        removeSessionCart();
    } else {
        saveCartToSession(store.state.cart.data);
    }
}

export const isValidSessionCart = (cart: Cart): boolean => {
    return Boolean(cart?.Products?.length > 0);
};

export const mapInitialCartValues = async (cart: Cart): Promise<Cart> => {
    cart.Customer.ReceiveNewsletter = getInitialNewsLetterState(cart);
    cart.ShoppingCartKey = getShoppingCartKeyFromUrl(cart);

    if (!configBase.sessionCartStorage.enabled || !isCheckoutPage()) {
        return cart;
    }
    try {
        return getCartUpdatedFromPreviousCart(cart);
    } catch (error) {
        await LogError(error as JavascriptError, StatsigEvent.CUSTOM_ERROR);
        return cart;
    }
};

export const mapCartValues = (cart: Cart): Cart => {
    cart.ProductCombination = getInitialProductCombination(cart);
    cart.ShoppingCartKey = getShoppingCartKeyFromUrl(cart);
    cart.Products = setProductDefaultValues(cart.Products); // Remove when backend adds it
    return cart;
};

function setProductDefaultValues(products: AnyProduct[]) {
    const discountIsAppliedDefaultValue = true;

    return products.map((p) =>
        p.AppliedDiscount
            ? {
                  ...p,
                  AppliedDiscount: {
                      ...p.AppliedDiscount,
                      IsApplied: p.AppliedDiscount.IsApplied ?? discountIsAppliedDefaultValue,
                  },
              }
            : p
    );
}

function getCartUpdatedFromPreviousCart(cart: Cart): Cart {
    if (canSetInitialValues(cart)) {
        const updatedCart = updatedValuesFromPreviousCart({ ...cart });
        const updatedProperties = getObjectDiff(cart, updatedCart);
        const updatedFields = updatedProperties ? Object.keys(updatedProperties) : [];
        logStatsigCheckoutCartSessionUpdate(cart, true, updatedFields);
        consoleLog(
            LogColorType.CartSessionUpdate,
            `Cart Session Updated properties`,
            updatedProperties
        );
        return updatedCart;
    } else {
        logStatsigCheckoutCartSessionUpdate(cart, false, []);
    }

    return cart;
}

export function isMainProduct(type: ProductType) {
    const mainTypes = [
        ProductType.Hotel,
        ProductType.Flight,
        ProductType.Ferry,
        ProductType.Activity,
    ];

    return mainTypes.includes(type);
}

export function getProductFromDate(p: AnyProduct, short = true): Date | null {
    let date = undefined;

    switch (p.Type) {
        case ProductType.Flight: {
            const departure = (p as FlightProduct).Legs[0].Segments[0].DepartureDate;
            date = departure ? convertToDate(departure) : null;
            break;
        }
        case ProductType.Ferry:
            date = convertToDate((p as FerryProduct).Legs[0].Segments[0].DepartureDate);
            break;
        case ProductType.Hotel:
        case ProductType.Golf:
            date = convertToDate((p as HotelProduct).FromDate);
            break;
        case ProductType.Activity:
            date = convertToDate((p as ActivityProduct).Date);
            break;
    }

    return date
        ? convertToDate(short ? getShortDate(date) : getFormattedDate(date, DateFormat.ISO))
        : null;
}

export function getProductToDate(p: AnyProduct, short = true): Date | null {
    let date = undefined;

    if (!p) {
        return null;
    }

    switch (p.Type) {
        case ProductType.Flight: {
            const product = p as FlightProduct;
            const leg = product.Legs[product.Legs.length - 1];
            const segment = leg.Segments[leg.Segments.length - 1];
            date = segment.ArrivalDate ? convertToDate(segment.ArrivalDate) : null;
            break;
        }
        case ProductType.Ferry: {
            const product = p as FerryProduct;
            const leg = product.Legs[product.Legs.length - 1];
            const segment = leg.Segments[0];
            date = segment.ArrivalDate ? convertToDate(segment.ArrivalDate) : null;
            break;
        }
        case ProductType.Hotel:
        case ProductType.Golf:
            date = convertToDate((p as HotelProduct).ToDate);
            break;
        case ProductType.Activity:
            date = convertToDate((p as ActivityProduct).Date);
            break;
    }

    return date
        ? convertToDate(short ? getShortDate(date) : getFormattedDate(date, DateFormat.ISO))
        : null;
}

export function getAgeRange(
    product: AnyProduct,
    pax: MainProductPax,
    ageDefinitions: AgeRangeDefinitions
): AgeRange {
    switch (product.Type) {
        case ProductType.Flight:
            return getAgeConfigByProduct(
                ageDefinitions,
                product.Type,
                (pax as FlightPax).PaxDescription?.Age
            );
        case ProductType.Ferry:
            return getAgeConfigByProduct(
                ageDefinitions,
                product.Type,
                (pax as FerryPax).PaxDescription?.Age
            );
        default:
            return getAgeConfigByProduct(ageDefinitions, product.Type, 18);
    }
}

export function showDiscountCodeField(cart: Cart) {
    const { ProductCombination, Affiliate } = cart;
    const isFlightOnly = ProductCombination?.Type === ProductCombinationType.Flight;

    return !isFlightOnly && !isChannelMetaHotel(Affiliate) && !isWhiteLabel();
}

export function getBookingIdFromToken(): string {
    const token = store.getters['cart/getBookingToken'];
    return token ? token.split(/[-|]/)[0] : '';
}

export const getItineraryTitle = ({ Legs }: ItineraryBookData): string => {
    return getFlightRoute(Legs) ?? '';
};

export const getFlightRoute = (legs: FlightLeg[]): string | null => {
    const outbound = legs[0];
    const firstSegment = outbound.Segments[0];
    const lastSegment = outbound.Segments[outbound.Segments.length - 1];
    const departureName = firstSegment.DepartureAirportName;
    const arrivalName = lastSegment.ArrivalAirportName;

    return departureName && arrivalName && `${departureName} - ${arrivalName}`;
};

export const getFlightRouteSummary = (
    legs: FlightLeg[]
): { from: string; to: string; label: string } | null => {
    const outbound = legs[0];
    const firstSegment = outbound.Segments[0];
    const lastSegment = outbound.Segments[outbound.Segments.length - 1];
    const departureCode = firstSegment.DepartureAirportCode;
    const arrivalCode = lastSegment.ArrivalAirportCode;

    return departureCode && arrivalCode
        ? {
              from: departureCode,
              to: arrivalCode,
              label: `${departureCode} - ${arrivalCode}`,
          }
        : null;
};
